import { Dispatch, SetStateAction } from 'react';
import type { Product } from '@/types/product';
import type { CartItem } from './useCartState';
import { supabase } from '@/integrations/supabase/client';
import { useCartDatabase } from './useCartDatabase';
import { toast } from '@/hooks/use-toast';

interface UseCartActionsProps {
  products: Product[];
  setCartItems: Dispatch<SetStateAction<CartItem[]>>;
  cartItems: CartItem[];
}

export const useCartActions = ({ products, setCartItems, cartItems }: UseCartActionsProps) => {
  const { clearCartFromDatabase } = useCartDatabase();

  const addToCart = (productId: string, quantity: number) => {
    console.log('Adding to cart:', { productId, quantity, availableProducts: products.length });

    const applyAdd = (product: Product) => {
      console.log('Found product:', product.product_name);
      setCartItems(currentItems => {
        const isFirstItem = currentItems.length === 0;
        const existingItemIndex = currentItems.findIndex(
          item => String(item.id) === String(productId)
        );

        let updatedItems: CartItem[];

        if (existingItemIndex > -1) {
          updatedItems = [...currentItems];
          updatedItems[existingItemIndex].quantity += quantity;
          console.log('Updated existing cart item quantity:', updatedItems[existingItemIndex]);
        } else {
          const newItem = { id: String(productId), quantity, product };
          console.log('Added new cart item:', newItem);
          updatedItems = [...currentItems, newItem];
          if (isFirstItem) {
            checkAuthAndNotify();
          }
        }

        return updatedItems;
      });
    };

    const cached = products.find(p => String(p.id) === String(productId));

    if (cached) {
      applyAdd(cached);
    } else {
      // Product not yet in cart — fetch it on demand (single row)
      supabase
        .from('products')
        .select(`
          *,
          product_images (*),
          category:product_categories(*),
          subcategory:product_subcategories(*)
        `)
        .eq('id', productId)
        .eq('is_active', true)
        .single()
        .then(({ data, error }) => {
          if (error || !data) {
            console.error('Product not found for ID:', productId, error);
            return;
          }
          applyAdd(data as Product);
        });
    }
  };

  const checkAuthAndNotify = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    console.log('🔔 Checking if should show first-item notification. User logged in:', !!session?.user);
    if (!session?.user) {
      console.log('🔔 Showing first-item notification for non-logged-in user');
      toast({
        title: "Ready to checkout?",
        description: "Remember to log in before checkout to save your cart and place orders. Items added now will be saved when you log in.",
        duration: 6000,
      });
    }
  };

  const removeFromCart = (productId: string) => {
    console.log('Removing from cart:', productId);
    setCartItems(currentItems => currentItems.filter(item => String(item.id) !== String(productId)));
  };

  const updateCartItemQuantity = (productId: string, quantity: number) => {
    if (quantity < 1) {
      removeFromCart(productId);
      return;
    }

    console.log('Updating cart item quantity:', { productId, quantity });
    setCartItems(currentItems =>
      currentItems.map(item =>
        String(item.id) === String(productId) ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = async () => {
    console.log('🗑️ Clearing cart completely');
    setCartItems([]);
    localStorage.removeItem('cart');

    try {
      await clearCartFromDatabase();
    } catch (error) {
      console.error('Failed to clear cart from database:', error);
    }
  };

  const debugCartAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    console.log('=== CART AUTH DEBUG ===');
    console.log('Current User ID:', session?.user?.id);
    console.log('User Email:', session?.user?.email);
    console.log('Cart Items Count:', cartItems.length);
    console.log('Cart Items:', cartItems.map(item => ({
      id: item.id,
      product: item.product.product_name,
      quantity: item.quantity
    })));
    console.log('localStorage cart:', localStorage.getItem('cart'));
    console.log('======================');
    return {
      userId: session?.user?.id,
      email: session?.user?.email,
      cartCount: cartItems.length,
      cartItems: cartItems
    };
  };

  return {
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    clearCart,
    debugCartAuth
  };
};
